import { Component, OnDestroy, OnInit, Input, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { ModelPoolService } from '../../../services/model-pool.service';
import { MuxCredentialService } from '../../../services/mux-credential.service';
import { ClaudeAuthWizardService } from '../../../services/claude-auth-wizard.service';
import { AntigravityAuthWizardService } from '../../../services/antigravity-auth-wizard.service';
import { CodexAuthWizardService } from '../../../services/codex-auth-wizard.service';
import { DialogService } from '../../../services/dialog.service';

type Step = 'vendor' | 'mode' | 'name' | 'key' | 'oauth';

interface VendorOption {
  id: MuxCredentialService.Vendor;
  label: string;
  blurb: string;
  modes: MuxCredentialService.Mode[];
  enabled: boolean;
}

/**
 * The Credentials pool — section ② of the Setup tab (Machine → Credentials → Agents).
 * Lists the user's provider credentials and hosts the "connect" flow whose second step
 * (Subscription vs Pay-per-token) is the pay-per-token on-ramp made physical.
 */
@Component({
  selector: 'app-credential-pool',
  templateUrl: './credential-pool.component.html',
  styleUrls: ['./credential-pool.component.scss'],
  standalone: false,
})
export class CredentialPoolComponent implements OnInit, OnDestroy {
  private dialogs = inject(DialogService);
  credentials: MuxCredentialService.Credential[] = [];
  loading = false;
  loadError = false;

  // create flow
  adding = false;
  step: Step = 'vendor';
  draft: { vendor: MuxCredentialService.Vendor; mode: MuxCredentialService.Mode; name: string; apiKey: string } = {
    vendor: 'anthropic',
    mode: 'subscription',
    name: '',
    apiKey: '',
  };
  submitting = false;

  readonly vendors: VendorOption[] = [
    { id: 'anthropic', label: 'Anthropic', blurb: "Claude — Max/Pro subscription or a pay-per-token API key", modes: ['subscription', 'api_key'], enabled: true },
    { id: 'google', label: 'Google', blurb: 'Gemini — an AI Studio API key or Antigravity sign-in', modes: ['api_key', 'subscription'], enabled: true },
    { id: 'openai', label: 'OpenAI', blurb: 'ChatGPT — an API key or a Team login (via Codex)', modes: ['api_key', 'subscription'], enabled: true },
  ];

  constructor(
    private creds: MuxCredentialService,
    private wizard: ClaudeAuthWizardService,
    private antigravityWizard: AntigravityAuthWizardService,
    private codexWizard: CodexAuthWizardService,
    private pool: ModelPoolService,
  ) {}

  async ngOnInit(): Promise<void> {
    await this.reload();
  }

  ngOnDestroy(): void {
    this._authSub?.unsubscribe();
    this._errSub?.unsubscribe();
    this.wizard.stopPolling();
    this.codexWizard.stopPolling();
  }

  // ── OAuth flow (subscription create + credential-detail re-auth) ──
  authing = false;
  authCid: string | null = null;
  authUrl: string | null = null;
  authCode = '';
  authStatus: string | null = null;
  authError: string | null = null;
  // Transient per-card notice (e.g. "already connected") shown when an auth click
  // resolves without a sign-in flow, so the click never looks dead. Keyed by cred id.
  authNotice: { cid: string; text: string } | null = null;
  submittingCode = false;
  private _authSub?: Subscription;
  private _errSub?: Subscription;

  // OAuth has three shapes: claude/antigravity use paste-code (URL + paste a code back);
  // codex (openai/subscription) uses device-auth (show URL + device code, then poll — no
  // paste-back). The target is derived from the create draft, or the selected credential.
  deviceCode: string | null = null; // shown for the codex device-auth flow
  private _oauthTarget: 'claude' | 'antigravity' | 'codex' = 'claude';
  private resolveOAuthTarget(): 'claude' | 'antigravity' | 'codex' {
    const vendor = this.adding ? this.draft.vendor : this.selectedCred?.vendor;
    const mode = this.adding ? this.draft.mode : this.selectedCred?.mode;
    if (vendor === 'google' && mode === 'subscription') return 'antigravity';
    if (vendor === 'openai' && mode === 'subscription') return 'codex';
    return 'claude';
  }
  get isDeviceAuth(): boolean { return this._oauthTarget === 'codex'; }

  async startOAuth(cid: string): Promise<void> {
    this.authCid = cid;
    this.authUrl = null;
    this.authCode = '';
    this.deviceCode = null;
    this.authStatus = null;
    this.authError = null;
    this.authNotice = null;
    this.authing = true;
    this._oauthTarget = this.resolveOAuthTarget();
    this._authSub?.unsubscribe();
    this._errSub?.unsubscribe();
    // Fresh session: the wizard's status is a BehaviorSubject and replays its last
    // value on subscribe. After a successful re-auth it still holds 'authenticated',
    // so the next Re-authenticate finished instantly on the stale value — panel gone,
    // real session polling unwatched (oum, 2026-09-02). Reset BEFORE subscribing.
    if (this._oauthTarget === 'antigravity') this.antigravityWizard.reset();
    else if (this._oauthTarget === 'codex') this.codexWizard.reset();
    else this.wizard.reset();
    const onStatus = (s: string | null) => {
      this.authStatus = s;
      if (s === 'authenticated') this.finishOAuth();
    };
    const onError = (e: string | null) => (this.authError = e);
    if (this._oauthTarget === 'antigravity') {
      this._authSub = this.antigravityWizard.authStatus$.subscribe(onStatus);
      this._errSub = this.antigravityWizard.error$.subscribe(onError);
    } else if (this._oauthTarget === 'codex') {
      this._authSub = this.codexWizard.authStatus$.subscribe(onStatus);
      this._errSub = this.codexWizard.error$.subscribe(onError);
    } else {
      this._authSub = this.wizard.authStatus$.subscribe(onStatus);
      this._errSub = this.wizard.error$.subscribe(onError);
    }
    try {
      if (this._oauthTarget === 'codex') {
        // Device-auth: show URL + device code, then poll until approved.
        const resp = await this.codexWizard.startAuth(cid);
        // The backend short-circuits when this credential's CODEX_HOME is already
        // valid: it returns status='authenticated' with NO device_url and never starts
        // a sign-in flow. finishOAuth() (via the status subscription) refreshes the
        // card, but the panel then vanishes with no word — so the click looks dead.
        // Surface an explicit "already connected" notice instead.
        if (resp.status === 'authenticated' && !resp.device_url) {
          this.authNotice = { cid, text: 'Already connected — no re-authentication needed.' };
          return;
        }
        this.authUrl = resp.device_url;
        this.deviceCode = resp.device_code;
        // window.open() after an await is treated as non-user-initiated and is often
        // popup-blocked (no tab, no console error). The panel's inline "Open sign-in"
        // link + device code are the reliable path; the tab is best-effort.
        if (this.authUrl) window.open(this.authUrl, '_blank');
        this.codexWizard.startPolling();
      } else if (this._oauthTarget === 'antigravity') {
        const resp = await this.antigravityWizard.startAuth();
        this.authUrl = resp.oauth_url;
        if (this.authUrl) window.open(this.authUrl, '_blank');
      } else {
        const resp = await this.wizard.startAuth(cid, this.currentTeam || 'local');
        this.authUrl = resp.oauth_url;
        if (this.authUrl) window.open(this.authUrl, '_blank');
        this.wizard.startPolling();
      }
    } catch {
      this.authError = this.authError || 'Failed to start sign-in.';
    }
  }

  async submitOAuthCode(): Promise<void> {
    if (!this.authCode.trim() || this.submittingCode) return;
    this.submittingCode = true;
    try {
      if (this._oauthTarget === 'antigravity') await this.antigravityWizard.submitCode(this.authCode.trim());
      else await this.wizard.submitCode(this.authCode.trim());
      // authStatus$ subscription flips to 'authenticated' → finishOAuth()
    } finally {
      this.submittingCode = false;
    }
  }

  private async finishOAuth(): Promise<void> {
    if (this._oauthTarget === 'claude') this.wizard.stopPolling();
    if (this._oauthTarget === 'codex') this.codexWizard.stopPolling();
    this._authSub?.unsubscribe();
    this._errSub?.unsubscribe();
    this.authing = false;
    this.authUrl = null;
    this.authCode = '';
    this.deviceCode = null;
    this.authError = null;
    this.adding = false; // close the create flow if it was open
    await this.reload();
    // Re-auth minted a fresh token, so the cached detail status is now stale. reload()
    // only refreshes the list; the expiry-aware detail status is fetched-once and cached
    // in `statuses[k]`, so re-fetch it for the credential we just authed (mirrors
    // rotateKey()). Without this the card shows the old status until a full browser refresh.
    if (this.authCid) {
      const c = this.credentials.find((x) => x.id === this.authCid);
      if (c) await this.fetchStatus(c);
    }
    this.creds.changed$.next();
  }

  cancelOAuth(): void {
    if (this._oauthTarget === 'antigravity') this.antigravityWizard.cancelAuth();
    else if (this._oauthTarget === 'codex') this.codexWizard.cancelAuth();
    else this.wizard.stopPolling();
    this._authSub?.unsubscribe();
    this._errSub?.unsubscribe();
    this.authing = false;
    this.authUrl = null;
    this.authCode = '';
    this.deviceCode = null;
    this.authError = null;
  }

  async reload(): Promise<void> {
    this.loading = true;
    this.loadError = false;
    try {
      const [all] = await Promise.all([this.creds.list(this.currentTeam || undefined), this.pool.pool(false, this.currentTeam || undefined).catch(() => null)]);
      // Team policy: a member sees only the credentials their team lets them bill to.
      this.credentials = this.pool.allowedCredentials(all);
    } catch {
      // API may not yet expose /credentials (pre-restart) — degrade to empty, not error-out.
      this.loadError = true;
      this.credentials = [];
    } finally {
      this.loading = false;
    }
  }

  /** Cards to display — hides the legacy flat-default row. Nothing binds to it
   *  anymore (the fallback is dead), so it isn't a real Credential. */
  @Input() teamName: string | null = null;   // the panel's venue (Agents app); fallback = ambient identity
  get currentTeam(): string | null {
    if (this.teamName) return this.teamName;
    try { return JSON.parse(localStorage.getItem('me') || '{}')?.selectedTeamName || null; } catch { return null; }
  }

  async scopeToTeam(c: MuxCredentialService.Credential): Promise<void> {
    if (!this.currentTeam || !c.id) return;
    try {
      await this.creds.scopeToTeam(c.id, this.currentTeam);
      await this.reload();
    } catch (e) { console.error('credential scope-to-team failed:', e); }
  }

  get poolCredentials(): MuxCredentialService.Credential[] {
    return this.credentials.filter((c) => !c.is_default);
  }

  get authedCount(): number {
    return this.poolCredentials.filter((c) => c.status === 'authed').length;
  }

  get selectedVendor(): VendorOption | undefined {
    return this.vendors.find((v) => v.id === this.draft.vendor);
  }

  modeLabel(m: MuxCredentialService.Mode): string {
    return m === 'subscription' ? 'Subscription' : m === 'api_key' ? 'Pay-per-token' : 'Local';
  }

  modeBlurb(m: MuxCredentialService.Mode): string {
    return m === 'subscription'
      ? 'Use an existing Max/Pro plan. Flat monthly cost; sign in once.'
      : m === 'api_key'
      ? 'Metered API key. Pay only for what you use; scales past a plan.'
      : 'Use ambient credentials on this machine.';
  }

  vendorLabel(v: MuxCredentialService.Vendor): string {
    return this.vendors.find((o) => o.id === v)?.label ?? v;
  }

  /** Example API-key prefix per vendor, for input placeholders. */
  keyHint(v: MuxCredentialService.Vendor): string {
    return v === 'google' ? 'AIza…' : v === 'openai' ? 'sk-…' : 'sk-ant-…';
  }

  /** Where to get an API key per vendor — the same destinations the legacy
   *  backend-detail "Get API Key" button used. */
  keyUrl(v: MuxCredentialService.Vendor): string {
    return v === 'google' ? 'https://aistudio.google.com/apikey'
      : v === 'openai' ? 'https://platform.openai.com/api-keys'
      : 'https://console.anthropic.com/settings/keys';
  }

  // ── Mode step: for Google the two modes are branded surfaces (AI Studio /
  //    Antigravity), not generic subscription/api_key. Antigravity's OAuth build
  //    isn't wired yet, so its mode is shown but disabled. ──
  modeCardTitle(m: MuxCredentialService.Mode): string {
    if (this.draft.vendor === 'google') return m === 'api_key' ? 'Google AI Studio' : 'Google Antigravity';
    if (this.draft.vendor === 'openai') return m === 'api_key' ? 'OpenAI API key' : 'ChatGPT Team login';
    return this.modeLabel(m);
  }

  modeCardBlurb(m: MuxCredentialService.Mode): string {
    if (this.draft.vendor === 'google') {
      return m === 'api_key'
        ? 'Gemini via a pay-per-token API key from AI Studio.'
        : "Google's agentic CLI (agy). Sign in with Google.";
    }
    if (this.draft.vendor === 'openai') {
      return m === 'api_key'
        ? 'Pay-per-token via an OpenAI API key (through Codex).'
        : 'Use your ChatGPT Team plan. Sign in via Codex device login.';
    }
    return this.modeBlurb(m);
  }

  /** True for modes whose backend isn't wired yet. (Antigravity is now wired.) */
  modeComingSoon(_m: MuxCredentialService.Mode): boolean {
    return false;
  }

  // ── credential detail (inline): status / expiry / scopes / rename / rotate ──
  detailKey: string | null = null;
  statuses: Record<string, MuxCredentialService.CredentialStatus | null> = {};
  statusLoading: Record<string, boolean> = {};
  nameEdit = '';
  keyEdit = '';
  savingDetail = false;

  keyOf(c: MuxCredentialService.Credential): string {
    return c.id ?? '__default__';
  }

  isOpen(c: MuxCredentialService.Credential): boolean {
    return this.detailKey === this.keyOf(c);
  }

  get selectedCred(): MuxCredentialService.Credential | undefined {
    return this.credentials.find((c) => this.keyOf(c) === this.detailKey);
  }

  closeDetail(): void {
    this.detailKey = null;
  }

  expiryOf(c: MuxCredentialService.Credential): string | null {
    return this.statusOf(c)?.token_details?.expires_in ?? null;
  }

  scopesOf(c: MuxCredentialService.Credential): string[] {
    return this.statusOf(c)?.token_details?.scopes ?? [];
  }

  async toggleDetail(c: MuxCredentialService.Credential): Promise<void> {
    const k = this.keyOf(c);
    if (this.detailKey === k) { this.detailKey = null; return; }
    this.detailKey = k;
    this.nameEdit = c.name;
    this.keyEdit = '';
    if (!(k in this.statuses)) await this.fetchStatus(c);
  }

  private async fetchStatus(c: MuxCredentialService.Credential): Promise<void> {
    const k = this.keyOf(c);
    this.statusLoading[k] = true;
    try {
      this.statuses[k] = await this.creds.status(c.id, c.vendor, this.currentTeam || 'local');
    } finally {
      this.statusLoading[k] = false;
    }
  }

  statusOf(c: MuxCredentialService.Credential): MuxCredentialService.CredentialStatus | null {
    return this.statuses[this.keyOf(c)] ?? null;
  }

  /** Rename a credential. Not allowed on the Default/flat credential — that's the
   *  flat-token→named migration, handled separately. */
  async saveName(c: MuxCredentialService.Credential): Promise<void> {
    const name = this.nameEdit.trim();
    if (!c.id || !name || name === c.name || this.savingDetail) return;
    this.savingDetail = true;
    try {
      await this.creds.update(c.id, { name }, this.currentTeam || 'local');
      await this.reload();
      this.creds.changed$.next();
    } catch {
      this.loadError = true;
    } finally {
      this.savingDetail = false;
    }
  }

  async rotateKey(c: MuxCredentialService.Credential): Promise<void> {
    if (!c.id || !this.keyEdit.trim() || this.savingDetail) return;
    this.savingDetail = true;
    try {
      // OpenAI's key runs through Codex (isolated CODEX_HOME); others store in the pool.
      if (c.vendor === 'openai') {
        await this.creds.codexLoginApiKey(this.keyEdit.trim(), c.id!);
      } else {
        await this.creds.setApiKey(c.id, this.keyEdit.trim(), this.currentTeam || 'local');
      }
      this.keyEdit = '';
      await this.fetchStatus(c);
      await this.reload();
      this.creds.changed$.next();
    } catch {
      this.loadError = true;
    } finally {
      this.savingDetail = false;
    }
  }

  // ── create flow ──
  startAdd(): void {
    this.adding = true;
    this.step = 'vendor';
    this.draft = { vendor: 'anthropic', mode: 'subscription', name: '', apiKey: '' };
  }

  cancelAdd(): void {
    this.adding = false;
  }

  pickVendor(v: VendorOption): void {
    if (!v.enabled) return;
    this.draft.vendor = v.id;
    // If the vendor offers a single mode, skip straight past the mode step.
    this.step = v.modes.length > 1 ? 'mode' : 'name';
    this.draft.mode = v.modes[0];
  }

  pickMode(m: MuxCredentialService.Mode): void {
    if (this.modeComingSoon(m)) return;
    this.draft.mode = m;
    this.step = 'name';
  }

  back(): void {
    if (this.step === 'key') this.step = 'name';
    else if (this.step === 'name') this.step = (this.selectedVendor?.modes.length ?? 1) > 1 ? 'mode' : 'vendor';
    else if (this.step === 'mode') this.step = 'vendor';
  }

  /** From the name step: metered credentials need an API key next; subscription creates now. */
  advanceFromName(): void {
    if (!this.draft.name.trim()) return;
    if (this.draft.mode === 'api_key') this.step = 'key';
    else this.submit();
  }

  get isMetered(): boolean {
    return this.draft.mode === 'api_key';
  }

  private slugify(name: string): string {
    return name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || `cred-${Date.now()}`;
  }

  async submit(): Promise<void> {
    if (!this.draft.name.trim() || this.submitting) return;
    if (this.isMetered && !this.draft.apiKey.trim()) return;
    this.submitting = true;
    try {
      const created = await this.creds.create({
        id: this.slugify(this.draft.name),
        name: this.draft.name.trim(),
        vendor: this.draft.vendor,
        mode: this.draft.mode,
      }, this.currentTeam || 'local');   // born where you are (the panel's venue)
      if (this.isMetered && created?.id) {
        // Metered: authenticate. OpenAI's api_key runs through Codex (writes
        // ~/.codex/auth.json); other vendors store the key in the pool.
        if (this.draft.vendor === 'openai') {
          await this.creds.codexLoginApiKey(this.draft.apiKey.trim(), created.id);
        } else {
          await this.creds.setApiKey(created.id, this.draft.apiKey.trim(), this.currentTeam || 'local');
        }
        this.adding = false;
        await this.reload();
        this.creds.changed$.next();
      } else if (created?.id) {
        // Subscription: authenticate via OAuth (stay in the flow).
        this.step = 'oauth';
        await this.startOAuth(created.id);
      } else {
        this.adding = false;
        await this.reload();
        this.creds.changed$.next();
      }
    } catch {
      // Surface a light inline failure without tearing down the flow.
      this.loadError = true;
    } finally {
      this.submitting = false;
    }
  }

  async remove(c: MuxCredentialService.Credential): Promise<void> {
    if (c.is_default || !c.id) return;
    if (!await this.dialogs.confirm(`Delete credential "${c.name}"? This removes its stored key/token.`)) return;
    try {
      await this.creds.remove(c.id, this.currentTeam || 'local');   // venue-explicit: a scoped credential is only deletable from its team
      this.detailKey = null;
      await this.reload();
      this.creds.changed$.next();
    } catch {
      this.loadError = true;
    }
  }
}
